/*
 * @Description:
 * @Author: Luke Z
 * @Date: 2021-04-01 16:01:53
 * @LastEditors: Luke Z
 * @LastEditTime: 2021-05-18 12:46:27
 * @FilePath: /unifyxp-v2-store-portal/src/tools/selectors/salesChannel.ts
 */
import { createSelector } from '@reduxjs/toolkit';

import { getStateTarget as getTarget } from 'src/tools/helper';
import { EnumModuleType } from 'src/types/common/module';

const getLoaded = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).loaded,

    (loaded) => {
      return { loaded };
    },
  );
};

const getProductName = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).name,

    (name) => {
      return name;
    },
  );
};

const getSubmitStamp = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).lastSubmitStamp,
    (status) => {
      return status;
    },
  );
};

const getCollapseStatus = (reducer, id) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).collapseOpen[id],
    (status) => {
      return status;
    },
  );
};

const getOrder = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).moduleContentOrder,
    (order) => {
      return order;
    },
  );
};

const getContent = (reducer, draggableId) => {
  return createSelector(
    (state: any) =>
      getTarget(state, reducer).moduleContentStaticConfig[draggableId],
    (state: any) =>
      getTarget(state, reducer).moduleContentDynamicConfig[draggableId],

    (staticData, dynamicData) => {
      return {
        staticData: staticData || {},
        dynamicData: dynamicData || [],
      };
    },
  );
};

const getOpeningCollapse = (reducer, _order?) => {
  return createSelector(
    (state: any) => {
      const order = _order
        ? _order
        : getTarget(state, reducer).moduleContentOrder || [];
      const _r = [];
      for (const item of order) {
        const { draggableId: id } = item;
        if (getTarget(state, reducer).collapseOpen[id]) {
          _r.push(id);
        }
      }
      return _r;
    },
    (status) => {
      return status;
    },
  );
};

const getOpeningCollapse2 = (reducer, _order?) => {
  return createSelector(
    (state: any) => {
      const _collapse = getTarget(state, reducer).collapseOpen || {};

      const _re = Object.keys(_collapse).filter((i) => _collapse[i]);

      return _re;
    },
    (openCollapse) => {
      return openCollapse;
    },
  );
};

const getVisibleCollapse = (reducer, _order?) => {
  return createSelector(
    (state: any) => {
      const _r = [];
      const order = _order
        ? _order
        : getTarget(state, reducer).moduleContentOrder || [];
      for (const item of order) {
        const { draggableId: id } = item;
        if (getTarget(state, reducer).moduleContentStaticConfig[id].visible) {
          _r.push(id);
        }
      }
      return _r;
    },
    (status) => {
      return status;
    },
  );
};

const getCollapseValidationMsgDisplay = (reducer, id) => {
  return createSelector(
    (state: any) => {
      return getTarget(state, reducer).collapseErrorMsgDisplay[id];
    },
    (status) => {
      return status;
    },
  );
};

const getCollapseOpenStatus = (reducer, id) => {
  return createSelector(
    (state: any) => {
      return getTarget(state, reducer).collapseOpen[id];
    },
    (open) => {
      return open;
    },
  );
};

const getPageEnabled = (reducer) => {
  return createSelector(
    (state: any) => {
      return getTarget(state, reducer).enabled;
    },
    (open) => {
      return open;
    },
  );
};

const getState = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer),
    (_s) => {
      return _s;
    },
  );
};

const getHasModified = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).hasModified,
    (hasModified) => {
      return hasModified;
    },
  );
};

const getLastModified = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).lastModified,
    (lastModified) => {
      return lastModified;
    },
  );
};

const getModifiedDate = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).modifiedDate,
    (lastModified) => {
      return lastModified;
    },
  );
};

const getSalesChannel = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).salesChannelContent,
    (hasModified) => {
      return hasModified;
    },
  );
};

const getSalesChannelName = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).salesChannelName,
    (hasModified) => {
      return hasModified;
    },
  );
};

const getGeneralFeaturedProduct = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).featuredProducts,
    (hasModified) => {
      return hasModified;
    },
  );
};

const getGeneralMiniProgramShare = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).miniProgramShare,
    (miniProgramShare) => {
      return miniProgramShare;
    },
  );
};

const getGeneralMiniProgramPageInfo = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).miniProgramPageInfo,
    (miniProgramPageInfo) => {
      return miniProgramPageInfo;
    },
  );
};

const getEntityUuid = (reducer) => {
  return createSelector(
    (state: any) => getTarget(state, reducer).uuid,
    (uuid) => {
      return uuid;
    },
  );
};

const getH1TagUuid = (reducer) => {
  return createSelector(
    (state: any) => {
      return getTarget(state, reducer).moduleContentStaticConfig;
    },
    (moduleContentStaticConfig) => {
      const texts = Object.values(moduleContentStaticConfig).filter(
        (i: any) => {
          const { moduleType } = i;
          return EnumModuleType.Text === moduleType;
        },
      );
      const h1TagEnabled: any = texts.find((i: any) => i.h1TagEnabled);
      if (h1TagEnabled) {
        return h1TagEnabled.draggableId;
      }
      return null;
    },
  );
};

export {
  getSalesChannel,
  getSalesChannelName,
  getGeneralFeaturedProduct,
  getHasModified,
  getCollapseValidationMsgDisplay,
  getCollapseOpenStatus,
  getState,
  getPageEnabled,
  getTarget,
  getContent,
  getOpeningCollapse,
  getVisibleCollapse,
  getCollapseStatus,
  getOrder,
  getSubmitStamp,
  getLoaded,
  getOpeningCollapse2,
  getLastModified,
  getProductName,
  getModifiedDate,
  getH1TagUuid,
  getGeneralMiniProgramShare,
  getGeneralMiniProgramPageInfo,
  getEntityUuid,
};
